import React, { useState } from "react";
import Navbar from "./Navbar";
import { toast } from "react-toastify";
import { FaPrint } from "react-icons/fa";
import logo1 from "../Assets/logo3.jpg";
import axiosInstance from "../config/axios";

const CommissionTrustLedger = () => {
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    fromDate: "",
    toDate: "",
  });

  // Fetch ledger entries for Commission Trust Account (10004)
  const fetchLedgerEntries = async () => {
    try {
      setLoading(true);
      console.log("Searching for ledger entries with params:", {
        fromDate: dateRange.fromDate,
        toDate: dateRange.toDate,
        accountNumber: "10004",
      });

      const response = await axiosInstance.get(`/ledger/account/10004`, {
        params: {
          fromDate: dateRange.fromDate,
          toDate: dateRange.toDate,
        },
      });

      console.log("Response received:", response.data);
      console.log("Number of entries found:", response.data.length);

      // Filter out duplicate EFT# summary entries
      const filteredEntries = response.data.filter((entry, index, array) => {
        // Always keep entries that have "Trade #:" in description (detailed entries)
        if (entry.description && entry.description.includes("Trade #:")) {
          return true;
        }

        // For EFT# summary entries, check if there's a corresponding detailed entry
        if (
          entry.description &&
          entry.description.match(/^EFT#\d+\s*-\s*.+$/)
        ) {
          // Extract EFT number from this entry
          const eftMatch = entry.description.match(/^EFT#(\d+)/);
          if (eftMatch) {
            const eftNumber = eftMatch[1];
            // Check if there's a detailed entry with the same EFT number in its reference field
            const hasDetailedEntry = array.some(
              (otherEntry) =>
                otherEntry.description &&
                otherEntry.description.includes(`Trade #:`) &&
                otherEntry.reference === `EFT#${eftNumber}`
            );
            // Only keep if there's no corresponding detailed entry
            return !hasDetailedEntry;
          }
        }

        // Keep all other entries
        return true;
      });

      console.log("Filtered entries:", filteredEntries.length);
      console.log(
        "Sample filtered entries:",
        filteredEntries.slice(0, 3).map((entry) => ({
          description: entry.description,
          reference: entry.reference,
          amount: entry.amount,
        }))
      );
      setLedgerEntries(filteredEntries);
    } catch (error) {
      console.error("Error fetching ledger entries:", error);
      console.error("Error details:", error.response?.data);
      toast.error("Failed to fetch ledger entries");
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (field, value) => {
    setDateRange((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSearch = () => {
    if (!dateRange.fromDate || !dateRange.toDate) {
      toast.error("Please select both from and to dates");
      return;
    }
    if (new Date(dateRange.fromDate) > new Date(dateRange.toDate)) {
      toast.error("From date cannot be after to date");
      return;
    }
    fetchLedgerEntries();
  };

  const handlePrint = () => {
    if (ledgerEntries.length === 0) {
      toast.error("No data to print. Please search for ledger entries first.");
      return;
    }
    window.print();
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="print:hidden">
        <Navbar />
      </div>
      <div className="flex">
        <div className="print:hidden">
        </div>
        <div className="flex-1 p-6">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 print:hidden">
                Commission Trust Ledger
              </h2>
              {ledgerEntries.length > 0 && (
                <button
                  onClick={handlePrint}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2 print:hidden"
                >
                  <FaPrint />
                  Print Report
                </button>
              )}
            </div>

            {/* Date Range Selection */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg print:hidden">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">
                Select Date Range
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    From Date
                  </label>
                  <input
                    type="date"
                    value={dateRange.fromDate}
                    onChange={(e) =>
                      handleDateRangeChange("fromDate", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    To Date
                  </label>
                  <input
                    type="date"
                    value={dateRange.toDate}
                    onChange={(e) =>
                      handleDateRangeChange("toDate", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    onClick={handleSearch}
                    disabled={loading}
                    className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? "Loading..." : "Search"}
                  </button>
                </div>
              </div>
            </div>

            {/* Print Header - Only visible when printing */}
            <div className="hidden print:block print:mb-6">
              {/* Main Title */}
              <div className="mb-4">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Commission Trust Ledger
                </h1>
              </div>

              {/* Company Info and Report Details */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center">
                  <img
                    src="/logo3.jpg"
                    alt="ABC Realty Inc. Logo"
                    className="w-12 h-12 mr-3"
                  />
                  <div>
                    <h2 className="text-lg font-bold text-gray-800">
                      ABC Realty Inc., Brokerage
                    </h2>
                    <p className="text-sm text-gray-600">
                      Commission Trust Ledger Report
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600 mb-1">
                    Report Date: {new Date().toLocaleDateString()}
                  </p>
                  <p className="text-sm text-gray-600">
                    Period: {dateRange.fromDate} to {dateRange.toDate}
                  </p>
                </div>
              </div>
            </div>

            {/* Print-only Table - Only visible when printing */}
            {ledgerEntries.length > 0 && (
              <div className="hidden print:block">
                <table className="w-full border-collapse border border-gray-400 text-xs">
                  <colgroup>
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "7%" }} />
                    <col style={{ width: "45%" }} />
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "14%" }} />
                  </colgroup>
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-400 px-2 py-1 text-center font-bold">
                        Date
                      </th>
                      <th className="border border-gray-400 px-2 py-1 text-center font-bold">
                        Reference
                      </th>
                      <th className="border border-gray-400 px-2 py-1 text-center font-bold">
                        Description
                      </th>
                      <th className="border border-gray-400 px-2 py-1 text-center font-bold">
                        Type
                      </th>
                      <th className="border border-gray-400 px-2 py-1 text-center font-bold">
                        Amount
                      </th>
                      <th className="border border-gray-400 px-2 py-1 text-center font-bold">
                        Trade Number
                      </th>
                      <th className="border border-gray-400 px-2 py-1 text-center font-bold">
                        Payee
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledgerEntries.map((entry, index) => (
                      <tr key={index}>
                        <td className="border border-gray-400 px-2 py-1 text-left">
                          {(() => {
                            const dateToUse =
                              entry.chequeDate || entry.date || entry.createdAt;
                            return dateToUse
                              ? new Date(dateToUse).toLocaleDateString()
                              : "-";
                          })()}
                        </td>
                        <td className="border border-gray-400 px-2 py-1 text-left">
                          {entry.reference || "N/A"}
                        </td>
                        <td className="border border-gray-400 px-2 py-1 text-left">
                          {(() => {
                            let description = entry.description || "";

                            // Remove unwanted portions that appear to be struck through
                            // Remove "Commission Trust - [address]" patterns
                            description = description.replace(
                              /\s*-\s*Commission Trust\s*-\s*[^-]+$/g,
                              ""
                            );

                            // Remove duplicate address patterns at the end
                            description = description.replace(
                              /\s*-\s*[^-]+Street[^-]*$/g,
                              ""
                            );

                            // Remove duplicate addresses (same address mentioned twice)
                            // Pattern: " - Address - Address" becomes " - Address"
                            description = description.replace(
                              /\s*-\s*([^-]+)\s*-\s*\1\s*$/g,
                              " - $1"
                            );

                            return description;
                          })()}
                        </td>
                        <td className="border border-gray-400 px-2 py-1 text-center">
                          {entry.type}
                        </td>
                        <td className="border border-gray-400 px-2 py-1 text-right">
                          ${parseFloat(entry.amount).toFixed(2)}
                        </td>
                        <td className="border border-gray-400 px-2 py-1 text-center">
                          {(() => {
                            // If tradeNumber is already set, use it
                            if (entry.tradeNumber) return entry.tradeNumber;

                            // Extract trade number from description
                            if (entry.description) {
                              const tradeMatch =
                                entry.description.match(/Trade #:\s*(\d+)/i);
                              if (tradeMatch) return tradeMatch[1];
                            }

                            return "N/A";
                          })()}
                        </td>
                        <td className="border border-gray-400 px-2 py-1 text-left">
                          {(() => {
                            // If payee is already set, use it
                            if (entry.payee) return entry.payee;

                            if (entry.type === "Debit" && entry.description) {
                              // Check for "Received From:" pattern first
                              const receivedFromMatch = entry.description.match(
                                /Received From:\s*([^-]+)/i
                              );
                              if (receivedFromMatch)
                                return receivedFromMatch[1].trim();

                              // Check for "EFT#XXXX - Company Name" pattern
                              const eftMatch =
                                entry.description.match(/EFT#\d+\s*-\s*(.+)$/);
                              if (eftMatch) return eftMatch[1].trim();
                            }
                            if (entry.type === "Credit" && entry.description) {
                              const m =
                                entry.description.match(/Paid to:\s*([^-]+)/i);
                              if (m) return m[1].trim();
                            }
                            return "N/A";
                          })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Print-only Totals */}
                <div className="mt-4 text-sm">
                  <div className="flex justify-start space-x-8">
                    <span className="font-semibold">
                      Total Debits: $
                      {ledgerEntries
                        .filter((entry) => entry.type === "Debit")
                        .reduce(
                          (sum, entry) => sum + parseFloat(entry.amount),
                          0
                        )
                        .toFixed(2)}
                    </span>
                    <span className="font-semibold">
                      Total Credits: $
                      {ledgerEntries
                        .filter((entry) => entry.type === "Credit")
                        .reduce(
                          (sum, entry) => sum + parseFloat(entry.amount),
                          0
                        )
                        .toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Screen-only Table */}
            {ledgerEntries.length > 0 && (
              <div className="space-y-4 print:hidden">
                <h3 className="text-lg font-semibold text-gray-700">
                  Ledger Entries
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full border border-gray-300 ledger-table table-fixed">
                    {/* Column widths tuned to match the screenshot */}
                    <colgroup>
                      <col style={{ width: "8%" }} /> {/* Date */}
                      <col style={{ width: "10%" }} /> {/* Reference */}
                      <col style={{ width: "38%" }} />{" "}
                      {/* Description (wide) */}
                      <col style={{ width: "8%" }} /> {/* Type (narrow) */}
                      <col style={{ width: "12%" }} /> {/* Amount (medium) */}
                      <col style={{ width: "8%" }} />{" "}
                      {/* Trade Number (narrow) */}
                      <col style={{ width: "16%" }} /> {/* Payee (wide) */}
                    </colgroup>

                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-center text-sm font-medium text-gray-700 border-b border-r">
                          Date
                        </th>
                        <th className="px-3 py-2 text-center text-sm font-medium text-gray-700 border-b border-r">
                          Reference
                        </th>
                        <th className="px-3 py-2 text-center text-sm font-medium text-gray-700 border-b border-r">
                          Description
                        </th>
                        <th className="px-3 py-2 text-center text-sm font-medium text-gray-700 border-b border-r">
                          Type
                        </th>
                        <th className="px-3 py-2 text-center text-sm font-medium text-gray-700 border-b border-r">
                          Amount
                        </th>
                        <th className="px-3 py-2 text-center text-sm font-medium text-gray-700 border-b border-r">
                          Trade Number
                        </th>
                        <th className="px-3 py-2 text-center text-sm font-medium text-gray-700 border-b">
                          Payee
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerEntries.map((entry, index) => (
                        <tr
                          key={index}
                          className="border-b hover:bg-gray-50 align-top"
                        >
                          <td className="px-3 py-2 text-sm text-left border-r">
                            {(() => {
                              const dateToUse =
                                entry.chequeDate ||
                                entry.date ||
                                entry.createdAt;
                              return dateToUse
                                ? new Date(dateToUse).toLocaleDateString()
                                : "-";
                            })()}
                          </td>
                          <td className="px-3 py-2 text-sm text-left border-r">
                            {entry.reference || "N/A"}
                          </td>
                          <td className="px-3 py-2 text-sm text-left border-r whitespace-pre-line break-words">
                            {(() => {
                              let description = entry.description || "";

                              // Remove unwanted portions that appear to be struck through
                              // Remove "Commission Trust - [address]" patterns
                              description = description.replace(
                                /\s*-\s*Commission Trust\s*-\s*[^-]+$/g,
                                ""
                              );

                              // Remove duplicate address patterns at the end
                              description = description.replace(
                                /\s*-\s*[^-]+Street[^-]*$/g,
                                ""
                              );

                              // Remove duplicate addresses (same address mentioned twice)
                              // Pattern: " - Address - Address" becomes " - Address"
                              description = description.replace(
                                /\s*-\s*([^-]+)\s*-\s*\1\s*$/g,
                                " - $1"
                              );

                              return description;
                            })()}
                          </td>
                          <td className="px-3 py-2 text-center border-r">
                            <span className="px-2 py-1 rounded text-xs bg-gray-100 text-black type-badge">
                              {entry.type}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-sm font-medium text-center border-r tabular-nums">
                            ${parseFloat(entry.amount).toFixed(2)}
                          </td>
                          <td className="px-3 py-2 text-sm text-center border-r">
                            {(() => {
                              // If tradeNumber is already set, use it
                              if (entry.tradeNumber) return entry.tradeNumber;

                              // Extract trade number from description
                              if (entry.description) {
                                const tradeMatch =
                                  entry.description.match(/Trade #:\s*(\d+)/i);
                                if (tradeMatch) return tradeMatch[1];
                              }

                              return "N/A";
                            })()}
                          </td>
                          <td className="px-3 py-2 text-sm text-left break-words">
                            {(() => {
                              // If payee is already set, use it
                              if (entry.payee) {
                                return entry.payee;
                              }

                              // For Debit transactions (money received), extract name from description
                              if (entry.type === "Debit" && entry.description) {
                                // Check for "Received From:" pattern first
                                const receivedFromMatch =
                                  entry.description.match(
                                    /Received From:\s*([^-]+)/i
                                  );
                                if (receivedFromMatch) {
                                  return receivedFromMatch[1].trim();
                                }

                                // Check for "EFT#XXXX - Company Name" pattern
                                const eftMatch =
                                  entry.description.match(
                                    /EFT#\d+\s*-\s*(.+)$/
                                  );
                                if (eftMatch) {
                                  return eftMatch[1].trim();
                                }
                              }

                              // For Credit transactions (money paid), extract name from description
                              if (
                                entry.type === "Credit" &&
                                entry.description
                              ) {
                                const paidToMatch =
                                  entry.description.match(
                                    /Paid to:\s*([^-]+)/i
                                  );
                                if (paidToMatch) {
                                  return paidToMatch[1].trim();
                                } else {
                                  // Fallback: try to extract name from other patterns
                                  const nameMatch = entry.description.match(
                                    /Trade #:?\s*\d+\s*-\s*([^-]+?)\s*-\s*[^-]+$/
                                  );
                                  if (nameMatch) {
                                    return nameMatch[1].trim();
                                  }
                                }
                              }

                              return "N/A";
                            })()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Summary */}
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="font-semibold">Total Debits: </span>
                      <span className="text-black font-medium">
                        $
                        {ledgerEntries
                          .filter((entry) => entry.type === "Debit")
                          .reduce(
                            (sum, entry) => sum + parseFloat(entry.amount),
                            0
                          )
                          .toFixed(2)}
                      </span>
                    </div>
                    <div>
                      <span className="font-semibold">Total Credits: </span>
                      <span className="text-black font-medium">
                        $
                        {ledgerEntries
                          .filter((entry) => entry.type === "Credit")
                          .reduce(
                            (sum, entry) => sum + parseFloat(entry.amount),
                            0
                          )
                          .toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {ledgerEntries.length === 0 && !loading && (
              <div className="text-center py-8">
                <p className="text-gray-500 text-lg">
                  No ledger entries found. Please select a date range and
                  search.
                </p>
              </div>
            )}

            {/* Print Footer - Only visible when printing */}
            <div className="hidden print:block print:mt-8 print:pt-4 print:border-t print:border-gray-300">
              <div className="flex justify-between items-center text-xs text-gray-600">
                <div>
                  <p>
                    https://homelifetopstar.brokeragelead.ca/commission-trust-ledger
                  </p>
                </div>
                <div>
                  <p>1/1</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommissionTrustLedger;
